import { TrendingUp, DollarSign, Building2, FileCheck } from "lucide-react";
import Reveal, { Stagger, StaggerItem } from "../motion/Reveal";
import { IndianTricolorBar, SaffronDivider } from "../decorations/IndianAccents";

const FCNR_FEATURES = [
  {
    title: "NRE to NRO Flow",
    description: "Convert foreign currency deposits into rupee accounts with zero compliance hassle",
    points: ["RBI Form compliance", "TCS tracking", "Real-time exchange rates"],
    icon: DollarSign,
    color: "zt-gold",
  },
  {
    title: "USD 1M Repatriation",
    description: "Move capital back home under RBI's liberalized repatriation scheme",
    points: ["CA-certified", "Bank coordinated", "Tax optimized"],
    icon: TrendingUp,
    color: "zt-sage",
  },
  {
    title: "Property Investment",
    description: "Invest FCNR funds directly in Indian real estate across tier-1 cities",
    points: ["FEMA compliant", "RERA verified", "8-10% yields"],
    icon: Building2,
    color: "zt-teal",
  },
  {
    title: "Tax Compliance",
    description: "Form 67, DTAA relief, and annual filings handled by our CA network",
    points: ["DTAA structuring", "Loss harvesting", "15CA/CB ready"],
    icon: FileCheck,
    color: "zt-sage",
  },
];

const getColorClass = (color) => {
  const colorMap = {
    "zt-gold": "hsl(var(--zt-gold))",
    "zt-sage": "hsl(var(--zt-sage))",
    "zt-teal": "hsl(var(--zt-teal))",
  };
  return colorMap[color];
};

export default function FCNRHighlight() {
  return (
    <section
      id="fcnr"
      className="py-24 lg:py-32 relative overflow-hidden"
      style={{ background: "rgba(255,255,255,0.22)" }}
    >
      {/* Decorative Indian accents - layered saffron and sage */}
      <div
        aria-hidden
        className="absolute top-10 right-[-5%] w-96 h-96 rounded-full opacity-[0.12] blur-3xl pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-10%] left-[-8%] w-80 h-80 rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 70%)",
        }}
      />
      {/* Saffron accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[hsl(var(--zt-gold))] to-transparent opacity-70" />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        {/* Header */}
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex justify-center mb-6">
            <IndianTricolorBar />
          </div>
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-1 w-12 bg-gradient-to-r from-[hsl(var(--zt-gold))] to-[hsl(var(--zt-sage))]" />
            <span className="eyebrow text-[hsl(var(--zt-gold))]">Core Offering</span>
            <span className="h-1 w-12 bg-gradient-to-l from-[hsl(var(--zt-gold))] to-[hsl(var(--zt-sage))]" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-[hsl(var(--zt-teal-deep))] mb-6 leading-tight">
            FCNR to India: Simplified
          </h2>
          <p className="text-lg text-[hsl(var(--zt-charcoal))]/80 font-body leading-relaxed max-w-2xl mx-auto">
            Whether you're managing foreign currency earnings, repatriating wealth, or investing in India's
            growth, we handle every compliance and tax nuance. NRIs deserve Indian fintech built for them.
          </p>
        </Reveal>

        <SaffronDivider />

        {/* FCNR Features Grid */}
        <Stagger
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16"
          staggerChildren={0.1}
        >
          {FCNR_FEATURES.map((feature, idx) => {
            const colorHSL = getColorClass(feature.color);
            return (
              <StaggerItem key={`fcnr-feature-${feature.title}`}>
                <div
                  className={`group relative h-full rounded-2xl border-2 border-[hsl(var(--zt-beige-deep))] bg-gradient-to-br from-white to-[hsl(var(--zt-beige))]/30 p-7 transition-all duration-300 hover:-translate-y-2 hover:border-[hsl(var(--zt-gold))]/60 hover:shadow-xl hover:shadow-[hsl(var(--zt-gold))]/10`}
                >
                  {/* Icon with gradient */}
                  <div
                    className="w-14 h-14 rounded-xl bg-gradient-to-br grid place-items-center mb-5 group-hover:scale-110 transition-transform"
                    style={{
                      background: `linear-gradient(135deg, ${colorHSL}15, transparent)`,
                    }}
                  >
                    <feature.icon
                      size={24}
                      style={{ color: colorHSL, opacity: 0.8 }}
                    />
                  </div>

                  <h3 className="font-heading text-lg font-bold text-[hsl(var(--zt-teal-deep))] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-[hsl(var(--zt-charcoal))]/75 font-body leading-relaxed mb-4">
                    {feature.description}
                  </p>

                  {/* Feature points */}
                  <ul className="space-y-2">
                    {feature.points.map((point) => (
                      <li key={point} className="flex items-start gap-2.5">
                        <span 
                          className="h-1.5 w-1.5 rounded-full flex-shrink-0 mt-1.5 opacity-60"
                          style={{ backgroundColor: colorHSL }}
                        />
                        <span className="text-xs text-[hsl(var(--zt-charcoal))]/70 font-heading">{point}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Hover indicator */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" 
                    style={{background: `linear-gradient(135deg, ${colorHSL}05, hsl(var(--zt-sage))/2)`}}
                  />
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>

        {/* Call to action stats */}
        <Reveal className="mt-20 grid md:grid-cols-4 gap-6">
          <div className="rounded-xl border-2 border-[hsl(var(--zt-gold))]/20 bg-gradient-to-br from-[hsl(var(--zt-gold))]/8 to-transparent p-8 text-center">
            <p className="text-4xl font-bold text-[hsl(var(--zt-gold))] font-numeric mb-2">$1M</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 font-heading">RBI Repatriation Limit</p>
          </div>
          <div className="rounded-xl border-2 border-[hsl(var(--zt-sage))]/20 bg-gradient-to-br from-[hsl(var(--zt-sage))]/8 to-transparent p-8 text-center">
            <p className="text-4xl font-bold text-[hsl(var(--zt-sage))] font-numeric mb-2">8-10%</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 font-heading">Property ROI</p>
          </div>
          <div className="rounded-xl border-2 border-[hsl(var(--zt-teal))]/20 bg-gradient-to-br from-[hsl(var(--zt-teal))]/8 to-transparent p-8 text-center">
            <p className="text-4xl font-bold text-[hsl(var(--zt-teal))] font-numeric mb-2">0%</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 font-heading">Compliance Fees</p>
          </div>
          <div className="rounded-xl border-2 border-[hsl(var(--zt-sage))]/20 bg-gradient-to-br from-[hsl(var(--zt-sage))]/8 to-transparent p-8 text-center">
            <p className="text-4xl font-bold text-[hsl(var(--zt-sage))] font-numeric mb-2">24h</p>
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/70 font-heading">Advisor Response</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
